import FleetTitle from '@/libs/antd/components/Title';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { Button, Col, DatePicker, Divider, Form, Row, Select, Table } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import Input from 'antd/es/input/Input';
import { FC, useState } from 'react';
import dayjs from 'dayjs';
import { useApp } from '@/App';
import { useModals } from '@/components/modals/ModalProvider';
import { useForm } from '@/libs/antd/form';
import RoboticStatusBadge from '@/pages/robotic/components/RoboticStatusBadge';
import { useQcCustomerSearch, useQcCustomerSearchOptions } from '@/libs/fleet-api/services/qc-customer';
import type { SearchQcCustomerRequest } from '@/libs/fleet-api/services/qc-customer-types';

const QCNewCustomerListPage: FC = () => {
	const { navigate } = useApp();
	const { openCustomerDetail } = useModals();

	const [queryParams, setQueryParams] = useState<SearchQcCustomerRequest>({
		pagination: { pageNumber: 1, pageSize: 10 },
	});

	const searchQuery = useQcCustomerSearch(queryParams);
	const optionsQuery = useQcCustomerSearchOptions();
	const isLoading = searchQuery.isFetching;

	const statusOptions = [
		{ label: 'Semua', value: '' },
		...(optionsQuery.data?.data?.statusOptions ?? []).map((o) => ({
			label: o.value ?? '',
			value: o.id ?? '',
		})),
	];

	const searchByOptions = (optionsQuery.data?.data?.searchByOptions ?? []).map((o) => ({
		label: o.value ?? '',
		value: o.id ?? '',
	}));

	const dataSource =
		searchQuery.data?.data?.items?.map((item, index) => ({
			...item,
			key: index + 1,
		})) ?? [];

	const { form, fieldName } = useForm<{
		dateFrom?: dayjs.Dayjs | null;
		dateTo?: dayjs.Dayjs | null;
		status?: string;
		searchBy?: string;
		keyword?: string;
	}>();

	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
			<FleetTitle>Quality Control New Customer</FleetTitle>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				labelCol={{ flex: 'none', style: { width: 150 } }}
				style={{ overflow: 'visible' }}
				onFinish={(values) => {
					setQueryParams({
						pagination: { pageNumber: 1, pageSize: 10 },
						search:
							values.keyword && values.searchBy
								? { by: values.searchBy, value: values.keyword }
								: undefined,
						dateFrom: values.dateFrom?.format('YYYY-MM-DD') ?? null,
						dateTo: values.dateTo?.format('YYYY-MM-DD') ?? null,
						status: values.status || null,
					});
				}}
			>
				<Row gutter={20}>
					<Col flex="none">
						<Form.Item label="Tanggal Activity">
							<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
								<Form.Item name={fieldName('dateFrom')} noStyle>
									<DatePicker format="DD/MM/YYYY" disabled={isLoading} />
								</Form.Item>
								<span>sd</span>
								<Form.Item name={fieldName('dateTo')} noStyle>
									<DatePicker format="DD/MM/YYYY" disabled={isLoading} />
								</Form.Item>
							</div>
						</Form.Item>
					</Col>
				</Row>
				<Divider style={{ margin: '20px 0' }} />
				<Row gutter={20}>
					<Col span={24}>
						<Form.Item
							name={fieldName('status')}
							initialValue=""
							label="Status"
						>
							<Select
								options={statusOptions}
								style={{ width: 160 }}
								disabled={isLoading}
								loading={optionsQuery.isLoading}
							/>
						</Form.Item>
					</Col>
				</Row>
				<Divider style={{ margin: '20px 0' }} />
				<Row gutter={20}>
					<Col flex="none">
						<Form.Item
							name={fieldName('searchBy')}
							initialValue={searchByOptions[0]?.value}
							label="Kata Kunci"
						>
							<Select
								options={searchByOptions}
								style={{ width: 200 }}
								disabled={isLoading}
								loading={optionsQuery.isLoading}
							/>
						</Form.Item>
					</Col>
					<Col flex="auto">
						<Form.Item name={fieldName('keyword')}>
							<Input placeholder="Search" disabled={isLoading} />
						</Form.Item>
					</Col>
					<Col flex="none" style={{ width: 190 }}>
						<Button
							block
							type="primary"
							htmlType="submit"
							disabled={isLoading}
						>
							SEARCH
						</Button>
					</Col>
				</Row>
			</Form>

			<Divider style={{ margin: 0 }} />

			<Table
				pagination={{
					total: searchQuery.data?.data?.totalCount,
					pageSize: queryParams.pagination.pageSize,
					current: queryParams.pagination.pageNumber,
					onChange: (pageNumber) => {
						setQueryParams((prev) => ({
							...prev,
							pagination: { ...prev.pagination, pageNumber },
						}));
					},
				}}
				scroll={{ x: 'max-content' }}
				bordered
				loading={isLoading}
				dataSource={dataSource}
				columns={[
					{
						title: 'Action',
						align: 'center',
						fixed: 'right',
						width: 1,
						render: (_, record) => {
							if (!record.isActionEnabled) return <ArrowRightOutlined style={{ color: '#bababa', cursor: 'not-allowed' }} />;
							return (
								<a
									onClick={(e) => {
										e.preventDefault();
										navigate('QCNewCustomerPage', {
											customerId: record.customerId!,
											mode: 'edit',
										});
									}}
								>
									<ArrowRightOutlined />
								</a>
							);
						}
					},
					{
						title: 'Customer ID',
						render: (_, record) =>
							record.customerId ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										openCustomerDetail(record.customerId!);
									}}
								>
									{record.customerId}
								</a>
							) : (
								'-'
							),
					},
					{
						title: 'NPWP',
						render: (_, record) => record.npwpNo || '-',
					},
					{
						title: 'Nama Customer',
						render: (_, record) =>
							record.customerName ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										openCustomerDetail(record.customerId!);
									}}
								>
									{record.customerName}
								</a>
							) : (
								'-'
							),
					},
					{
						title: 'Last Modified',
						render: (_, record) =>
							record.lastModified
								? dayjs(record.lastModified).format('DD/MM/YYYY HH:mm')
								: '-',
					},
					{
						title: 'Detail',
						align: 'center',
						render: (_, record) => {
							const disabled = record.isActionEnabled;
							return (
								<a
									className="trigger-popup"
									onClick={(e) => {
										e.preventDefault();
										if (disabled) return;
										navigate('QCNewCustomerPage', {
											customerId: record.customerId!,
											mode: 'view',
										});
									}}
									style={disabled ? { cursor: 'not-allowed', color: '#bababa' } : undefined}
								>
									View Detail
								</a>
							);
						},
					},
					{
						title: 'Status',
						render: (_, record) =>
							record.status ? (
								<RoboticStatusBadge status={record.status as 'Pending' | 'Finish'} />
							) : (
								'-'
							),
					},
				]}
			/>
		</div>
	);
};

export default QCNewCustomerListPage;
